import type { Property } from '@vibesites/schema';
import { resolveFieldPath, type ContentOverride } from '@vibesites/runtime';
import { useProperty, usePropertyPhone, useScopedContentOverride } from '@vibesites/runtime/react';

/** Last resort when neither call tracking nor the property phone is set. */
export const PHONE_PLACEHOLDER = '(555) 555-5555';

function explicitPhone(property: Property, override: ContentOverride | undefined): string | null {
  if (!override) return null;
  if (override.overrideType === 'VALUE') return override.customValue?.trim() || null;
  if (override.overrideType === 'GENERATOR') return override.generatedValue?.trim() || null;
  if (override.overrideType === 'FIELD_MAPPING' && override.mappedField) {
    return resolveFieldPath(property, override.mappedField)?.trim() || null;
  }
  return null;
}

/**
 * Phone for a content token.
 * An editor override wins. Otherwise: call-tracking number, then the property
 * phone, then a placeholder. The token resolver cannot express that middle
 * step — an empty call-tracking field would jump straight to defaultValue.
 */
export function useDisplayPhone(tokenPath: string): string {
  const property = useProperty();
  const override = useScopedContentOverride(tokenPath);
  const propertyPhone = usePropertyPhone().trim();
  return explicitPhone(property, override) || propertyPhone || PHONE_PLACEHOLDER;
}
